import { faArrowDown, faArrowUp, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';

import { Column, DataTable, DataTableContainer, TableActionButton } from 'components/DataTable';
import { useApi } from '../../../hooks/useApi';
import { useConfirmedAction } from 'hooks/useConfirmedAction';
import { useModal } from 'hooks/useModal';
import { useCallback, useRef } from 'react';
import { SpaceGroupForm } from '.';
import { SpaceGroup } from '../types';
import { Button } from '@ui';

interface Props {
    clubSlug: string;
}

export const SpaceGroupList = ({ clubSlug }: Props) => {
    const [setModal, resetModal] = useModal();
    const confirm = useConfirmedAction();
    const api = useApi();
    const asyncList = useRef<any>(undefined);

    const handleShowSpaceGroupForm = useCallback(
        (group?: SpaceGroup) =>
            setModal({
                title: group ? `Platzgruppe "${group.name}" bearbeiten` : 'Platzgruppe erstellen',
                content: (
                    <SpaceGroupForm
                        clubSlug={clubSlug}
                        group={group}
                        callback={() => {
                            asyncList.current?.reload?.();
                            resetModal();
                        }}
                    />
                ),
                show: true,
                size: 100
            }),
        []
    );

    const handleDeleteSpaceGroup = useCallback((group: SpaceGroup) => {
        api({
            url: `space/group/${group.id}`,
            method: 'DELETE',
            onSuccess: () => asyncList.current?.reload?.()
        }).catch(() => {});
    }, []);

    const handleMoveSpaceGroup = useCallback((space: SpaceGroup, up: boolean) => {
        // NOTE: We are assuming sorted input from the DB here
        const spaces = asyncList.current?.items as SpaceGroup[];

        // Figure out where in the list the targeted space lies
        const targetIndex = spaces.findIndex((s) => s.id === space.id);

        space = spaces[targetIndex];

        if ((targetIndex >= spaces.length - 1 && !up) || (targetIndex === 0 && up)) {
            // Since we cannot move an object out of bounds, we can nop these calls here
            return;
        }

        // Switch positions with the competing space
        const swappingSpace = spaces[targetIndex + (up ? -1 : 1)];

        // Set the target space to the old position of the space above
        api({
            url: `space/group/${space.id}/swap/${swappingSpace.id}`,
            method: 'PUT',
            muted: true,
            onSuccess: () => asyncList.current?.reload?.()
        });
    }, []);

    const columns: Column<SpaceGroup>[] = [
        {
            uid: 'name',
            name: 'Name'
        },
        {
            uid: 'actions',
            name: 'Optionen',
            align: 'end'
        }
    ];

    return (
        <DataTableContainer>
            <Button className="tw-py-5" onPress={() => handleShowSpaceGroupForm()}>
                Platzgruppe erstellen
            </Button>
            <DataTable
                tableProps={{
                    sticked: true,
                    bordered: true,
                    borderWeight: 'light',
                    compact: false
                }}
                hideSearchBar
                asyncListRef={asyncList}
                columns={columns}
                fetchUrl={`space/group/${clubSlug}`}
                sortingFn={(a, b) => a.position - b.position}
                rowActions={(item) => (
                    <TableActionButton.Container justify="flex-end">
                        <TableActionButton icon={faArrowDown} onPress={() => handleMoveSpaceGroup(item, false)} />
                        <TableActionButton icon={faArrowUp} onPress={() => handleMoveSpaceGroup(item, true)} />
                        <TableActionButton icon={faEdit} onClick={() => handleShowSpaceGroupForm(item)} />
                        <TableActionButton icon={faTrash} onClick={() => confirm(() => handleDeleteSpaceGroup(item))} />
                    </TableActionButton.Container>
                )}
            />
        </DataTableContainer>
    );
};

export default SpaceGroupList;
